import {Epic, ofType} from 'redux-observable';
import {concat, Observable, of} from 'rxjs';
import {ajax, AjaxError} from 'rxjs/ajax';
import {catchError, filter, mergeMap, throttleTime, timeout} from 'rxjs/operators';
import {Action, AnyAction} from 'redux';
import * as CalculatorActions from '../../../actions/CalculatorActions';
import * as WizardMXActions from '../../../actions/WizardMXActions';
import * as UIActions from '../../../actions/UIActions';
import * as MC from '../../../constants/MotorcreditConstants';
import * as ErrorConstants from '../../../constants/ErrorConstants';
import {COUNTRIES} from '../../../constants/AppConstants';
import {processApiError} from '../../../utils/ApiUtil';
import {addCredentialAndExecute} from '../../../utils/RetryUtil';
import {State} from '../../../types/mx/reducers';
import {isEmpty} from '../../../../common/utils/ValidationUtils';
import * as ValidateConstant from '../../../constants/ValidateConstant';
import {getFirstMonthlyPayments} from '../../../utils/WizardResponseMapperUtils';
import {BankTarget} from '../../../types/mx/types';
import {Catalog} from '../../../../common/types/common';
import * as WizardActions from '../../../actions/WizardActions';

export const shouldCalculateQuotasMX: Epic<AnyAction, any, State> = (action$, state$): Observable<Action> =>
    action$.pipe(
        ofType(
            WizardMXActions.ON_LOAD_TURBO_CASH_SIMULATOR_MX_ACTION.name,
            WizardMXActions.ON_LOAD_SIMULATOR_PROFILE_MX_ACTION.name,
            CalculatorActions.YEAR_SELECTED_ACTION.name,
            CalculatorActions.BRAND_SELECTED_ACTION.name,
            CalculatorActions.MODEL_SELECTED_ACTION.name,
            CalculatorActions.VERSION_SELECTED_ACTION.name,
            CalculatorActions.PROVINCE_SELECTED_ACTION.name,
            CalculatorActions.CHANGE_POSTAL_CODE_ACTION.name,
            CalculatorActions.CHANGE_PRICE_ACTION.name,
            CalculatorActions.CHANGE_DOWN_PAYMENT_ACTION.name,
            CalculatorActions.CHANGE_DOWN_PAYMENT_SIMULATOR_PROFILE_ACTION.name,
            CalculatorActions.HAS_INSURANCE_SELECTED_ACTION.name,
            CalculatorActions.HAS_INSURANCE_SELECTED_SIMULATOR_PROFILE_ACTION.name
        ),
        filter(() => {
            const state: State = state$.value;
            const calculator = state.creditoReal.calculator;
            const target: BankTarget = calculator.bank;
            const fieldErrorCalculator = state.creditoReal.ui.calculatorStepView.fieldErrors;
            return calculator.postalCode.length === 5 &&
                fieldErrorCalculator.postalCodeIsValid &&
                target === BankTarget.CREDITO_REAL ||
                target === BankTarget.TURBO_CASH;
        }),
        throttleTime(100),
        mergeMap((action) => {
            const state: State = state$.value;
            const calculator = state.creditoReal.calculator;
            const target: BankTarget = calculator.bank;
            const profile: boolean = action.profile || false;
            const country = state.countrySelector.countrySelected;
            const applicantHome = state.creditoReal.loanApplication.applicantHome;
            const globalValidations = target === BankTarget.CREDITO_REAL ?
                profile ? state.globalValidations.configByProfile : state.globalValidations.creditoReal
                : state.globalValidations.turboCash;
            const minPrice = globalValidations.minAllowedVehiclePrice;
            const minDownPayment = globalValidations.minDownPayment;
            const maxDownPayment = globalValidations.maxDownPayment;
            const yearSelected = action.year ? action.year : calculator.yearSelected;
            const versionSelected = action.version ? action.version : calculator.versionSelected;
            const provinceHome: Catalog = {...applicantHome.state, payload: applicantHome.postalCode};
            const postalCode =
                target === BankTarget.CREDITO_REAL ?
                    {payload: state.creditoReal.calculator.postalCode} : calculator.provinceSelected;
            const provinceSelected =
                target === BankTarget.TURBO_CASH ?
                    provinceHome : profile ?
                        provinceHome : postalCode;
            const price = action.price ? action.price : calculator.price;
            const downPayment = action.downPayment ? action.downPayment : calculator.downPayment;
            const hasInsurance = action.hasInsurance ? action.hasInsurance : calculator.hasInsurance;

            const actions: AnyAction[] = [];
            const priceError = ValidateConstant.ERROR_PRICE(price, minPrice, country);
            actions.push(CalculatorActions.CHANGE_PRICE_ACTION.action(price, priceError));
            const downPaymentError = price > 0 ? ValidateConstant.ERROR_DOWN_PAYMENT(
                price, downPayment, minDownPayment, maxDownPayment, country
            ) : '';
            actions.push(CalculatorActions.CHANGE_DOWN_PAYMENT_ACTION.action(downPayment, downPaymentError));
            if (!isEmpty(provinceSelected.payload) && !isEmpty(versionSelected.payload) && !isEmpty(yearSelected.id) &&
                !isEmpty(price) && !isEmpty(downPayment) &&
                isEmpty(ValidateConstant.ERROR_PRICE(price, minPrice, country)) &&
                isEmpty(price > 0 && ValidateConstant.ERROR_DOWN_PAYMENT(
                    price, downPayment, minDownPayment, maxDownPayment, country
                ))
            ) {
                actions.push(CalculatorActions.CALCULATE_QUOTAS_MX_ACTION.action(
                    provinceSelected.payload,
                    versionSelected.payload,
                    yearSelected.id,
                    hasInsurance,
                    price,
                    downPayment,
                    profile,
                    target
                ));
            } else {
                actions.push(CalculatorActions.CLEAN_QUOTAS_MX_ACTION
                    .action(calculator.brandSelected.id, calculator.modelSelected.id, calculator.versionSelected.id));
            }

            return concat(...actions.map(item => of(item)));
        })
    );

export const calculateQuotasMX: Epic<AnyAction, any, State> = (action$, state$): Observable<Action> =>
    action$.pipe(
        ofType(CalculatorActions.CALCULATE_QUOTAS_MX_ACTION.name),
        throttleTime(100),
        mergeMap((action: AnyAction) => {
            const body = {
                postalCode: action.postalCode,
                version: action.version,
                vehiclePrice: parseFloat(action.price),
                year: parseInt(action.year, 10),
                downPayment: parseFloat(action.entry),
                hasInsurance: action.hasInsurance,
                isAdjustParameters: !!action.profile,
                isOnline: true
            };
            const state = state$.value;
            const bank: string = action.target;
            const bankState = bank === BankTarget.TURBO_CASH ?
                BankTarget.TURBO_CASH_STATE : BankTarget.CREDITO_REAL_STATE;
            const token = state[bankState].loanApplication.token ? `/${state[bankState].loanApplication.token}` : '';
            const bankSelected: string = bank === BankTarget.TURBO_CASH ?
                BankTarget.TURBO_CASH : BankTarget.CREDITO_REAL;
            const server = MC.MCC_URL_WITH_BANK(COUNTRIES.MX.id, bankSelected);
            const profile = action.profile;
            const suffix = profile ? '-by-profile' : '';
            const url = `${server}/banks${profile ? token : ''}/quota${suffix}`;
            const monthlyPaymentSelected = state[bankState].calculator.monthlyPayment;
            return concat(
                of(UIActions.IS_LOADING_ACTION.action(CalculatorActions.GOT_QUOTAS_MX_ACTION.name)),

                addCredentialAndExecute(headers => ajax.post(url, body, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => {
                        const warningMessages = response.response.notifications.warningMessages;
                        const data = response.response.data;
                        const state = state$.value;
                        const firstMonthlyPayment =
                            bank === BankTarget.CREDITO_REAL ?
                                getFirstMonthlyPayments(data, monthlyPaymentSelected) :
                                state.turboCash.calculator.monthlyPayment;
                        const hasInsurance = data.some(x => x.insuranceAmount.value > 0);
                        const failInsurance  = !hasInsurance || warningMessages.includes(ErrorConstants.NO_BITUAJ_RESULT_WARNING)
                        return concat(
                            of(refreshTokenObs),
                            of(
                                UIActions.IS_LOADED_ACTION.action(CalculatorActions.GOT_QUOTAS_MX_ACTION.name)
                            ),
                            of(CalculatorActions.GOT_QUOTAS_MX_ACTION.action(data,failInsurance)),
                            of(CalculatorActions.MONTHLY_PAYMENT_SELECTED_ACTION.action(firstMonthlyPayment, null))
                        );
                    }),
                    catchError((error: AjaxError) => {
                        return processApiError([
                            UIActions.IS_LOADED_ACTION.action(CalculatorActions.GOT_QUOTAS_MX_ACTION.name),
                            UIActions.ERROR_GENERAL_ACTION.action(error, action, url, body, 'POST')
                        ])(error);
                    })
                )
            );
        })
    );

export const validatePostalCodeAndSetState: Epic<AnyAction, any, State> = (action$, state$): Observable<Action> =>
    action$.pipe(
        ofType(
            CalculatorActions.CHANGE_POSTAL_CODE_ACTION.name
        ),
        filter(action => action.postalCode.length === 5),
        mergeMap((action: AnyAction) => {
            const url = `${MC.MCC_URL(COUNTRIES.MX.id)}/catalogs/locations/${action.postalCode}`;
            return concat(
                of(UIActions.IS_LOADING_ACTION.action(
                    WizardActions.CHANGE_CALCULATOR_POSTAL_CODE_ACTION.name)
                ),
                addCredentialAndExecute(headers => ajax.get(url, headers), state$.value).pipe(
                    timeout(MC.TIMEOUT),
                    mergeMap(({response, refreshTokenObs}: any) => concat(
                        of(refreshTokenObs),
                        of(CalculatorActions.VALIDATE_POSTAL_CODE_ACTION.action(true)),
                        of(CalculatorActions.PROVINCE_SELECTED_ACTION.action(response.response.data.state, null)),
                        of(UIActions.IS_LOADED_ACTION.action(WizardActions.CHANGE_CALCULATOR_POSTAL_CODE_ACTION.name))
                    )),
                    catchError((error) => {
                        console.log('entra a error ON_CHANGE_POSTAL_CODE SIMULATOR-------------: ', error);
                        const errorIsNotFoundLocationError = Array.isArray(error) ?
                            error.includes(ErrorConstants.NOT_FOUND_LOCATION_ERROR) : false;

                        return processApiError([
                            errorIsNotFoundLocationError ?
                                CalculatorActions.VALIDATE_POSTAL_CODE_ACTION.action(false) :
                                UIActions.ERROR_GENERAL_ACTION.action(error, action, url, {}, 'GET'),
                            UIActions.IS_LOADED_ACTION.action(WizardActions.CHANGE_CALCULATOR_POSTAL_CODE_ACTION.name)
                        ])(error);
                    })
                )
            );
        })
    );
